// src/pages/KingdomPage.jsx  ("Mission Moon": replaces the Kingdom island)
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { FaArrowLeft, FaLightbulb, FaSyncAlt, FaTrophy, FaClock, FaRocket, FaFlag } from "react-icons/fa";
import { db } from "../firebase";
import { useAuth } from "../authContext";
import BottomNav from "./BottomNav";
import "./KingdomPage.css";

/* ---------- Easy-to-change settings ---------- */
const IDEA_ROUTE = (id) => `/view-idea/${id}`; // where tapping a piece goes
const POST_ROUTE = "/add"; // where "Start rebuilding" goes
// FRESH START: only activity on/after this date counts. Month is 0-based (9 = October).
const SEASON_START = new Date(2026, 9, 21).getTime();
const TOTAL = 20; // rocket pieces
const WEIGHT = { post: 1, evolve: 1, comp: 2 }; // pieces earned per activity
const STAGES = ["Frame", "Body", "Power", "Finishing"]; // 5 pieces each
const DAY = 86400000;

/* ---------- Helpers ---------- */
const toMs = (v) => {
  if (!v) return 0;
  if (typeof v.toMillis === "function") return v.toMillis();
  if (v instanceof Date) return v.getTime();
  return typeof v === "number" ? v : 0;
};
const ago = (ms) => {
  const d = Math.floor((Date.now() - ms) / DAY);
  if (d <= 0) return "Today";
  if (d === 1) return "Yesterday";
  if (d < 30) return `${d} days ago`;
  if (d < 365) return `${Math.floor(d / 30)} mo ago`;
  return `${Math.floor(d / 365)} yr ago`;
};
const moodFor = (ms) => {
  if (!ms) return null;
  const d = Math.floor((Date.now() - ms) / DAY);
  if (d <= 2) return { k: "bright", label: "Bright skies", haze: 0 };
  if (d <= 7) return { k: "calm", label: "Calm skies", haze: 0 };
  if (d <= 21) return { k: "hazy", label: "Hazy", haze: 0.25 };
  return { k: "dusty", label: "Dusty", haze: 0.45 };
};
const sinceLabel = new Date(SEASON_START).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

/* ---------- Rocket art: 20 pieces, filled in this order ---------- */
const S = { stroke: "#1a1d3d", strokeWidth: 2.2, strokeLinejoin: "round" };
const HU = "url(#kgH)", CO = "url(#kgC)", MT = "url(#kgM)", GD = "url(#kgG)";
const Leg = ({ d }) => (
  <g fill="none" strokeLinecap="round">
    <path d={d} stroke="#1a1d3d" strokeWidth="9" />
    <path d={d} stroke="#c8cde8" strokeWidth="4.5" />
  </g>
);
// [name, home centre x, home centre y, z-order, drawing]
const RAW = [
  ["Engine nozzle", 200, 447, 3, <path d="M176 432h48l10 30h-68z" fill={MT} {...S} />],
  ["Lower hull", 200, 386, 3, <rect x="160" y="340" width="80" height="92" fill={HU} {...S} />],
  ["Left fin", 138, 415, 2, <path d="M160 378l-44 74 44-20z" fill={CO} {...S} />],
  ["Right fin", 262, 415, 2, <path d="M240 378l44 74-44-20z" fill={CO} {...S} />],
  ["Middle hull", 200, 299, 3, <rect x="160" y="258" width="80" height="82" fill={HU} {...S} />],
  ["Upper hull", 200, 224, 3, <rect x="160" y="190" width="80" height="68" fill={HU} {...S} />],
  ["Window", 200, 285, 6, (
    <g>
      <circle cx="200" cy="285" r="19" fill="#2b2f55" {...S} />
      <circle cx="200" cy="285" r="13" fill="#7fd6ff" />
      <path d="M193 279a9 9 0 0 1 9-4" stroke="#fff" strokeWidth="2.5" fill="none" strokeLinecap="round" />
    </g>
  )],
  ["Wiring band", 200, 334, 5, (
    <g>
      <rect x="160" y="328" width="80" height="12" fill="#2b2f55" {...S} />
      <path d="M166 334h68" stroke="#ffb347" strokeWidth="2" strokeDasharray="6 5" />
    </g>
  )],
  ["Fuel stripe", 200, 405, 5, <rect x="160" y="398" width="80" height="14" fill={CO} {...S} />],
  ["Mission badge", 200, 224, 6, (
    <g>
      <circle cx="200" cy="224" r="15" fill={GD} {...S} />
      <text x="200" y="230" textAnchor="middle" fontSize="17" fontWeight="800" fill="#1a1d3d">T</text>
    </g>
  )],
  ["Left booster", 141, 382, 1, (
    <g>
      <rect x="126" y="330" width="30" height="104" rx="14" fill={HU} {...S} />
      <rect x="126" y="372" width="30" height="10" fill={CO} {...S} />
    </g>
  )],
  ["Right booster", 259, 382, 1, (
    <g>
      <rect x="244" y="330" width="30" height="104" rx="14" fill={HU} {...S} />
      <rect x="244" y="372" width="30" height="10" fill={CO} {...S} />
    </g>
  )],
  ["Left thruster", 141, 442, 1, <path d="M130 434h22l-4 16h-14z" fill={MT} {...S} />],
  ["Right thruster", 259, 442, 1, <path d="M248 434h22l-4 16h-14z" fill={MT} {...S} />],
  ["Engine core", 200, 447, 5, <circle cx="200" cy="447" r="9" fill="#ffd36b" {...S} />],
  ["Nose cone", 200, 143, 4, <path d="M160 190Q172 118 200 96Q228 118 240 190z" fill={CO} {...S} />],
  ["Beacon", 200, 88, 5, (
    <g>
      <path d="M200 98V86" stroke="#1a1d3d" strokeWidth="3" />
      <circle className="kg-blink" cx="200" cy="80" r="6" fill="#ffe27a" {...S} />
    </g>
  )],
  ["Left landing leg", 145, 448, 1, <Leg d="M168 416l-30 60M122 478h32" />],
  ["Right landing leg", 255, 448, 1, <Leg d="M232 416l30 60M246 478h32" />],
  ["Gold trim", 200, 189, 5, <rect x="156" y="184" width="88" height="10" rx="4" fill={GD} {...S} />],
];
// Each piece rests as debris on the ground (spread across the crash site) until earned.
const PARTS = RAW.map(([name, hx, cy, z, el], i) => ({
  i, name, z, el,
  dx: 35 + ((i * 7) % 20) * 17.4 - hx,
  dy: 498 + (i % 2) * 22 - cy,
  r: ((i * 53) % 70) - 35,
}));
const ORDER = [...PARTS].sort((a, b) => a.z - b.z);

const STARS = (() => {
  let s = 7;
  const r = () => (s = (s * 16807) % 2147483647) / 2147483647;
  return Array.from({ length: 110 }, () => ({ x: -500 + r() * 1400, y: -1700 + r() * 2030, r: 0.6 + r() * 1.4, t: r() < 0.3 }));
})();

const DEFS = (
  <defs>
    <linearGradient id="kgH"><stop offset="0" stopColor="#fff" /><stop offset="1" stopColor="#cfd5ee" /></linearGradient>
    <linearGradient id="kgC"><stop offset="0" stopColor="#ff8a62" /><stop offset="1" stopColor="#e0452c" /></linearGradient>
    <linearGradient id="kgM"><stop offset="0" stopColor="#8a90ad" /><stop offset="1" stopColor="#565c7a" /></linearGradient>
    <linearGradient id="kgG"><stop offset="0" stopColor="#ffe27a" /><stop offset="1" stopColor="#e9a82a" /></linearGradient>
    <linearGradient id="kgMoon" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#f4f1e6" /><stop offset="1" stopColor="#aaa696" /></linearGradient>
    <linearGradient id="kgGlow" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#ff9a6a" stopOpacity="0" /><stop offset="1" stopColor="#ff9a6a" stopOpacity=".6" /></linearGradient>
    <radialGradient id="kgEarth" cx=".4" cy=".35" r=".7"><stop offset="0" stopColor="#8fd3ff" /><stop offset="1" stopColor="#1f5fb8" /></radialGradient>
  </defs>
);

/* ---------- Scene ---------- */
const Scene = ({ n, pieces, home, name, open }) => {
  const onKey = (e, id) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      open(id);
    }
  };
  return (
    <svg className="kg-svg" viewBox="0 0 400 640" role="group" aria-label="Rocket rebuild progress">
      {DEFS}
      <g className="kg-cam">
        {STARS.map((s, i) => (
          <circle key={i} className={s.t ? "kg-tw" : undefined} style={s.t ? { animationDelay: `${(i % 7) * 0.4}s` } : undefined} cx={s.x} cy={s.y} r={s.r} fill="#fff" opacity=".85" />
        ))}
        <rect x="-1400" y="300" width="3200" height="200" fill="url(#kgGlow)" />

        {/* The goal, visible from Earth */}
        <g className="kg-gm">
          <circle cx="338" cy="80" r="22" fill="url(#kgMoon)" />
          <circle cx="330" cy="74" r="5" fill="#b8b3a3" opacity=".6" />
          <circle cx="346" cy="90" r="3" fill="#b8b3a3" opacity=".6" />
        </g>

        {/* The Moon (far above) with Earth in its sky */}
        <path d="M-759 -722A2400 2400 0 0 1 1159 -722V-400H-759z" fill="url(#kgMoon)" />
        <g fill="#b8b3a3" opacity=".6">
          <ellipse cx="110" cy="-892" rx="30" ry="8" />
          <ellipse cx="330" cy="-880" rx="38" ry="9" />
          <ellipse cx="205" cy="-858" rx="20" ry="6" />
        </g>
        <circle cx="84" cy="-1290" r="24" fill="url(#kgEarth)" />
        <circle cx="84" cy="-1290" r="30" fill="#8fd3ff" opacity=".12" />

        {/* Earth: hills, ground, launch pad and gantry */}
        <path d="M-1400 490V455Q-1000 410 -600 450T-100 440Q150 420 330 450T900 440Q1300 420 1800 460V490z" fill="#0e1330" />
        <rect x="-1400" y="490" width="3200" height="1000" fill="#141a3d" />
        <rect x="-1400" y="490" width="3200" height="6" fill="#2b3566" />
        <path d="M92 490V300h14V490M92 330h40M92 380h36M92 430h34" stroke="#3a4170" strokeWidth="6" fill="none" />
        <circle className="kg-blink" cx="99" cy="294" r="4" fill="#ff5a4a" />
        <rect x="132" y="478" width="136" height="12" rx="3" fill="#3a4170" {...S} />

        {/* Moon flag, planted on arrival */}
        <g transform="translate(300 -920)">
          <g className="kg-flag">
            <path d="M0 0V-74" stroke="#e9e6dc" strokeWidth="3" strokeLinecap="round" />
            <path d="M1.5 -72h58q-8 16 0 32h-58z" fill="#ff7a52" stroke="#1a1d3d" strokeWidth="2" />
            <text x="30" y="-52" textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff">{(name || "Trescend").slice(0, 10)}</text>
          </g>
        </g>

        {/* Launch smoke */}
        <g className="kg-smoke">
          {[0, 1, 2, 3, 4].map((i) => (
            <circle key={i} cx={200 + (i - 2) * 28} cy="478" r="16" fill="#dfe3ff" style={{ "--sx": `${(i - 2) * 22}px`, animationDelay: `${i * 0.25}s` }} />
          ))}
        </g>

        {/* The rocket */}
        <g className="kg-rkt">
          <g className="kg-shake">
            <g className="kg-fl">
              <path d="M184 462Q200 560 216 462z" fill="#ff9d3a" />
              <path d="M190 462Q200 520 210 462z" fill="#fff0a0" />
            </g>
            {PARTS.slice(n, n + 3).map((p) => (
              <g key={`g${p.i}`} className="kg-ghost">{p.el}</g>
            ))}
            {ORDER.map((p) => {
              const on = p.i < n;
              const piece = pieces[p.i];
              const at = on && home;
              return (
                <g
                  key={p.i}
                  className={`kg-p${on ? " kg-on" : ""}`}
                  style={{ transform: at ? "none" : `translate(${p.dx}px, ${p.dy}px) rotate(${p.r}deg)`, transitionDelay: at ? `${p.i * 70}ms` : "0ms" }}
                  {...(on
                    ? { role: "button", tabIndex: 0, "aria-label": `${p.name}: open ${piece.title}`, onClick: () => open(piece.ideaId), onKeyDown: (e) => onKey(e, piece.ideaId) }
                    : {})}
                >
                  {on && <title>{`${p.name}: ${piece.title}`}</title>}
                  {p.el}
                </g>
              );
            })}
          </g>
        </g>
      </g>
    </svg>
  );
};

/* ---------- Shared UI pieces (wooden board + ribbon, troop-card tile) ---------- */
const Board = ({ title, children }) => (
  <div className="cc-board">
    <h2 className="cc-ribbon"><span>{title}</span></h2>
    {children}
  </div>
);
const Tile = ({ Icon, plus, label, c }) => (
  <div className={`ab-tile ${c}`}>
    {plus && <span className="ab-plus">{plus}</span>}
    <span className="ab-ic"><Icon /></span>
    <b>{label}</b>
  </div>
);

/* ---------- About tab ---------- */
const About = ({ n }) => (
  <section className="kg-panel kg-wrap">
    <Board title="Mission Moon">
      <div className="ab-sky">
        <svg viewBox="0 -24 320 144" aria-hidden="true">
          <defs>
            <radialGradient id="kgEarth" cx=".4" cy=".35" r=".7"><stop offset="0" stopColor="#8fd3ff" /><stop offset="1" stopColor="#1f5fb8" /></radialGradient>
            <linearGradient id="kgMoon" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#f4f1e6" /><stop offset="1" stopColor="#aaa696" /></linearGradient>
          </defs>
          {[[20, -10], [90, -14], [150, 6], [210, -16], [300, 20], [120, 70]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r="1.6" fill="#fff" opacity=".8" />)}
          <circle cx="50" cy="96" r="30" fill="url(#kgEarth)" />
          <circle cx="268" cy="34" r="20" fill="url(#kgMoon)" />
          <path d="M70 70Q150 -10 252 30" fill="none" stroke="#f4be3a" strokeWidth="3" strokeDasharray="2 8" strokeLinecap="round" />
          <path d="M150 20l8 18-8-4-8 4z" fill="#ff7a52" stroke="#2a1a0e" strokeWidth="1.5" strokeLinejoin="round" transform="rotate(62 150 30)" />
          <path d="M268 14V-6" stroke="#e9e6dc" strokeWidth="2" />
          <path d="M268 -6h12l-3 5 3 5h-12z" fill="#ff7a52" stroke="#2a1a0e" strokeWidth="1.2" />
        </svg>
      </div>
      <p className="cc-lead">Your rocket crashed on Earth. Everything you do in Trescend brings one piece back. Rebuild all {TOTAL} and it launches!</p>
    </Board>

    <Board title="Earn pieces">
      <div className="ab-grid">
        <Tile Icon={FaLightbulb} plus="+1" label="Post an idea" c="gold" />
        <Tile Icon={FaSyncAlt} plus="+1" label="Evolve an idea" c="blue" />
        <Tile Icon={FaTrophy} plus="+2" label="Enter a contest" c="red" />
      </div>
      <p className="cc-foot">Evolve a different idea each time. Repeats don't count.</p>
    </Board>

    <Board title="Four stages">
      <ol className="ab-steps">
        {STAGES.map((s, k) => (
          <li key={s} className={n >= (k + 1) * 5 ? "done" : n > k * 5 ? "now" : ""}>
            <i>{k + 1}</i>
            <em aria-hidden="true">{"★".repeat(k + 1)}</em>
            <b>{s}</b>
            <span>{k * 5 + 1} to {(k + 1) * 5}</span>
          </li>
        ))}
      </ol>
    </Board>

    <Board title="Launch day">
      <div className="ab-grid">
        <Tile Icon={FaClock} label="Countdown" c="purple" />
        <Tile Icon={FaRocket} label="Liftoff" c="gold" />
        <Tile Icon={FaFlag} label="Moon flag" c="green" />
      </div>
    </Board>

    <Board title="Good to know">
      <ul className="ab-list">
        <li>Everyone starts fresh. Only activity since {sinceLabel} counts.</li>
        <li>Earned pieces never disappear. Quiet days only make the sky hazy.</li>
        <li>Tap any built piece to open the idea behind it.</li>
        <li>This is Trescend's very first version, so the journey pauses at the Moon. More planets arrive with the next launch!</li>
      </ul>
    </Board>
  </section>
);

/* ---------- Page ---------- */
const KingdomPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { currentUser } = useAuth();
  const targetUid = params.userId || currentUser?.uid || null;
  const isOwn = !params.userId || params.userId === currentUser?.uid;
  const flagKey = `kg-launched-${targetUid}`;

  const [tab, setTab] = useState("rocket");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [ideas, setIdeas] = useState([]);
  const [roads, setRoads] = useState([]);
  const [ownerName, setOwnerName] = useState("");
  const [ready, setReady] = useState(false);
  const [armed, setArmed] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | count | fly | landed
  const [snap, setSnap] = useState(true); // true = no transitions (instant jumps)
  const [count, setCount] = useState(0);
  const timers = useRef([]);
  const later = (fn, ms) => timers.current.push(setTimeout(fn, ms));
  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  useEffect(() => clearTimers, []);

  useEffect(() => {
    if (!targetUid) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setReady(false);
      setArmed(false);
      setError("");
      try {
        const [pub, comp, roadSnap, userSnap] = await Promise.all([
          getDocs(query(collection(db, "postedIdeas"), where("ownerId", "==", targetUid))),
          getDocs(query(collection(db, "competitionIdeas"), where("ownerId", "==", targetUid))),
          getDocs(collection(db, "users", targetUid, "kingdomRoads")),
          getDoc(doc(db, "users", targetUid)),
        ]);
        if (cancelled) return;
        const mapIdea = (d, kind) => ({ id: d.id, kind, title: d.data().title || "Untitled", t: toMs(d.data().createdAt) });
        setIdeas([...pub.docs.map((d) => mapIdea(d, "post")), ...comp.docs.map((d) => mapIdea(d, "comp"))]);
        setRoads(roadSnap.docs.map((d) => {
          const x = d.data();
          return { id: d.id, ideaId: x.ideaId || d.id, title: x.title || "Untitled", by: x.ownerName || "", t: toMs(x.createdAt) };
        }));
        const u = userSnap.exists() ? userSnap.data() : {};
        setOwnerName(u.username || u.displayName || u.name || "");
      } catch (err) {
        console.error("Mission load failed:", err);
        if (!cancelled) setError("Could not load this mission. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [targetUid]);

  // Only activity since SEASON_START counts, oldest first
  const events = useMemo(() => [
    ...ideas.filter((i) => i.t >= SEASON_START).map((i) => ({ id: `i${i.id}`, ideaId: i.id, t: i.t, type: i.kind, title: i.title })),
    ...roads.filter((r) => r.t >= SEASON_START).map((r) => ({ id: `r${r.id}`, ideaId: r.ideaId, t: r.t, type: "evolve", title: r.title, by: r.by })),
  ].sort((a, b) => a.t - b.t), [ideas, roads]);

  const pieces = useMemo(() => {
    const out = [];
    events.forEach((e) => { for (let k = 0; k < WEIGHT[e.type]; k++) out.push(e); });
    return out.slice(0, TOTAL);
  }, [events]);

  const n = pieces.length;
  const complete = n >= TOTAL;
  const stage = Math.min(4, Math.ceil(Math.max(n, 1) / 5));
  const mood = moodFor(events.length ? events[events.length - 1].t : 0);
  const open = (id) => navigate(IDEA_ROUTE(id));

  // Once data is in: jump straight to the Moon if already launched (or if visiting), else wait on the pad
  useEffect(() => {
    if (loading) return undefined;
    let seen = false;
    try { seen = !!localStorage.getItem(flagKey); } catch (e) { /* storage unavailable */ }
    setSnap(true);
    setPhase(complete && (!isOwn || seen) ? "landed" : "idle");
    setReady(true);
    const t = setTimeout(() => { setSnap(false); setArmed(true); }, 250);
    return () => clearTimeout(t);

  }, [loading]);

  const launch = () => {
    clearTimers();
    setSnap(false);
    setPhase("count");
    setCount(3);
    later(() => setCount(2), 800);
    later(() => setCount(1), 1600);
    later(() => { setCount(0); setPhase("fly"); }, 2400);
    later(() => setPhase("landed"), 2400 + 6500);
    try { localStorage.setItem(flagKey, "1"); } catch (e) { /* storage unavailable */ }
  };
  const skip = () => { clearTimers(); setSnap(true); setCount(0); setPhase("landed"); };
  const replay = () => { clearTimers(); setSnap(true); setPhase("idle"); later(launch, 80); };

  const log = useMemo(() => [...events].reverse().slice(0, 30), [events]);
  const logText = (e) =>
    e.type === "comp" ? `Entered "${e.title}" in the competition`
      : e.type === "evolve" ? `Evolved "${e.title}"${e.by ? ` by ${e.by}` : ""}`
        : `Posted "${e.title}"`;

  const tabs = [["rocket", "Rocket"], ["growth", "Recent growth"], ["about", "About"]];

  return (
    <div className={`kg-page tab-${tab}`}>
      <header className="kg-top">
        <div className="kg-wrap">
          <div className="kg-row">
            {!isOwn && (
              <button type="button" className="kg-back" aria-label="Go back" onClick={() => navigate(-1)}><FaArrowLeft /></button>
            )}
            <h1>{isOwn ? "Mission Moon" : `${ownerName ? `${ownerName}'s` : "Their"} Mission`}</h1>
           
          </div>
          <div className="kg-prog">
            <div className="kg-bar" role="progressbar" aria-valuemin={0} aria-valuemax={TOTAL} aria-valuenow={n}>
              <i style={{ width: `${(n / TOTAL) * 100}%` }} />
              {[25, 50, 75].map((p) => <b key={p} style={{ left: `${p}%` }} />)}
            </div>
            <span>{complete ? "Ready for the Moon" : `${n}/${TOTAL} pieces, ${STAGES[stage - 1]}`}</span>
          </div>
          <nav className="kg-tabs" role="tablist" aria-label="Mission sections">
            {tabs.map(([k, label]) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? "on" : ""} onClick={() => setTab(k)}>{label}</button>
            ))}
          </nav>
        </div>
      </header>

      {tab === "rocket" && (
        loading || !ready ? (
          <div className="kg-status">{error || "Loading mission…"}</div>
        ) : (
          <section className={`kg-scene ph-${phase}${snap ? " snap" : ""}`} aria-live="polite">
            <div className="kg-fit">
              <Scene n={n} pieces={pieces} home={armed || phase === "landed"} name={ownerName} open={open} />
            </div>
            {mood && mood.haze > 0 && <div className="kg-haze" style={{ opacity: mood.haze }} />}
            {phase === "count" && count > 0 && <div className="kg-count" key={count}>{count}</div>}
            {(phase === "count" || phase === "fly") && <button type="button" className="kg-skip" onClick={skip}>Skip</button>}


            <div className="kg-dock">
              {n === 0 && phase === "idle" && (
                <div className="kg-note">
                  {isOwn ? (
                    <>
                      <h2>Your rocket crashed</h2>
                      <p>Let your ideas out to rebuild it, piece by piece.</p>
                      <button type="button" className="kg-cta" onClick={() => navigate(POST_ROUTE)}>Start rebuilding</button>
                    </>
                  ) : (
                    <h2>This rocket hasn't been rebuilt yet</h2>
                  )}
                </div>
              )}
              {complete && phase === "idle" && isOwn && (
                <button type="button" className="kg-go" onClick={launch}><FaRocket aria-hidden="true" /> Launch</button>
              )}
              {phase === "landed" && (
                <div className="kg-note">
                  <h2>Mission complete: you reached the Moon!</h2>
                  <p>This is Trescend's very first version, so our journey pauses here. New planets are being charted for the next launch. Keep your ideas fueled, because the adventure continues.</p>
                  {isOwn && <button type="button" className="kg-link" onClick={replay}>Replay launch</button>}
                </div>
              )}
            </div>
          </section>
        )
      )}

      {tab === "growth" && (
        <section className="kg-panel kg-wrap">
          <Board title="Recent growth">
            {log.length === 0 ? (
              <p className="cc-lead">No activity yet. Your first post, evolve or competition entry adds the first piece.</p>
            ) : (
              <ul className="kg-log">
                {log.map((e) => (
                  <li key={e.id}>
                    <button type="button" onClick={() => open(e.ideaId)}>
                      <span className={`kg-ic kg-${e.type}`}>+{WEIGHT[e.type]}</span>
                      <span className="kg-lt">{logText(e)}</span>
                      <time>{ago(e.t)}</time>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="cc-foot">Fresh start: only activity since {sinceLabel} counts.{complete ? " Extra activity is saved for the next version." : ""}</p>
          </Board>
        </section>
      )}

      {tab === "about" && <About n={n} />}

      <BottomNav />
    </div>
  );
};

export default KingdomPage;